import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import EPageHeader from '@/components/everridge/EPageHeader';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminOwnerMainTab from '@/features/superAdmin/features/data/owners/components/SuperAdminOwnerMainTab';
import SuperAdminDataPaginatedTable from '@/features/superAdmin/features/data/shared/components/SuperAdminDataPaginatedTable';
import { ownerApartmentNestedColumns } from '@/features/superAdmin/features/data/owners/components/SuperAdminOwnersPageColumns';
import { useSuperAdminOwnerDetailApi } from '@/features/superAdmin/features/data/owners/hooks/useSuperAdminOwnerDetailApi';

type OwnerDetailTab = 'main' | 'apartments';

const SuperAdminOwnerDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { owner, isLoading } = useSuperAdminOwnerDetailApi(id);
    const [activeTab, setActiveTab] = useState<OwnerDetailTab>('main');

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate('/superAdmin/data/owners');

    const tabs: { id: OwnerDetailTab; label: string }[] = [
        { id: 'main', label: t('superAdmin.data.owners.tabs.main') },
        { id: 'apartments', label: t('superAdmin.data.owners.tabs.apartments') },
    ];

    const pageTitle = owner?.name
        ? `${t('superAdmin.data.owners.detail.title')} — ${owner.name}`
        : t('superAdmin.data.owners.detail.title');

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={pageTitle}>
                <Button variant="outline" className="gap-2" onClick={goBack}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.owners.backToList')}
                </Button>
            </EPageHeader>

            {isLoading && !owner ? (
                <div className="flex flex-1 items-center justify-center py-12">
                    <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
            ) : !owner ? (
                <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6 text-sm">
                    {t('elements.table.body.empty')}
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 border-b border-popover-100 pb-2">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                variant={activeTab === tab.id ? 'default' : 'outline'}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>

                    {activeTab === 'main' && <SuperAdminOwnerMainTab owner={owner} />}

                    {activeTab === 'apartments' && (
                        <SuperAdminDataPaginatedTable
                            columns={ownerApartmentNestedColumns}
                            data={owner.apartments}
                        />
                    )}
                </>
            )}
        </section>
    );
};

export default SuperAdminOwnerDetailPage;
